import {
  AlloyLegacyThemeDesignTokens,
  EmptyStateDesignTokens,
  WeaveTheme,
} from '../types';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const GAP_DEFAULT = '24px';
const GAP_XS = '8px';
const PADDING_X_WIDE = '40px';
const PADDING_X_NARROW = '16px';
const DESCRIPTION_MARGIN_TOP = '8px';

export const getEmptyStateTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): EmptyStateDesignTokens => ({
  container: {
    'background-color': theme.tokens.colors.background.primary.value,
    color: theme.tokens.colors.text.primary.value,
    'padding-top': '20px',
    'padding-bottom': '20px',
    'min-width': undefined,
    'max-width': undefined,
  },
  'illustration-container': {
    'max-height': undefined,
    'margin-top': '0',
    'margin-bottom': '0',
    'margin-left': '0',
    'margin-right': '0',
  },
  xs: {
    container: {
      'padding-left': PADDING_X_NARROW,
      'padding-right': PADDING_X_NARROW,
    },
    'illustration-container': { height: '50px', width: '50px' },
    headline: {
      'font-family': theme.typography.bodyMedium.fontFamily,
      'font-weight': '400',
      'font-size': theme.typography.bodyMedium.fontSize,
      'line-height': theme.typography.bodyMedium.lineHeight,
      'margin-top': GAP_XS,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
    description: {
      'font-family': theme.typography.bodySmall.fontFamily,
      'font-weight': String(theme.typography.bodySmall.fontWeight),
      'font-size': theme.typography.bodySmall.fontSize,
      'line-height': theme.typography.bodySmall.lineHeight,
      'margin-top': GAP_XS,
      color: undefined,
    },
    'actions-container': {
      'margin-top': GAP_XS,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
  },
  sm: {
    container: {
      'padding-left': PADDING_X_NARROW,
      'padding-right': PADDING_X_NARROW,
    },
    'illustration-container': { height: '100px', width: '100px' },
    headline: {
      'font-family': theme.typography.heading2.fontFamily,
      'font-weight': String(theme.typography.heading2.fontWeight),
      'font-size': theme.typography.heading2.fontSize,
      'line-height': '22px',
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
    description: {
      'font-family': theme.typography.bodySmall.fontFamily,
      'font-weight': String(theme.typography.bodySmall.fontWeight),
      'font-size': theme.typography.bodySmall.fontSize,
      'line-height': theme.typography.bodySmall.lineHeight,
      'margin-top': DESCRIPTION_MARGIN_TOP,
      color: undefined,
    },
    'actions-container': {
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
  },
  m: {
    container: {
      'padding-left': PADDING_X_WIDE,
      'padding-right': PADDING_X_WIDE,
    },
    'illustration-container': { height: undefined, width: undefined },
    headline: {
      'font-family': theme.typography.heading2.fontFamily,
      'font-weight': String(theme.typography.heading2.fontWeight),
      'font-size': theme.typography.heading2.fontSize,
      'line-height': theme.typography.heading2.lineHeight,
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
    description: {
      'font-family': theme.typography.bodyMedium.fontFamily,
      'font-weight': String(theme.typography.bodyMedium.fontWeight),
      'font-size': theme.typography.bodyMedium.fontSize,
      'line-height': theme.typography.bodyMedium.lineHeight,
      'margin-top': DESCRIPTION_MARGIN_TOP,
      color: undefined,
    },
    'actions-container': {
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
  },
  l: {
    container: {
      'padding-left': PADDING_X_WIDE,
      'padding-right': PADDING_X_WIDE,
    },
    'illustration-container': { height: '250px', width: '250px' },
    headline: {
      'font-family': theme.typography.title.fontFamily,
      'font-weight': String(theme.typography.title.fontWeight),
      'font-size': theme.typography.title.fontSize,
      'line-height': theme.typography.title.lineHeight,
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
    description: {
      'font-family': theme.typography.bodyMedium.fontFamily,
      'font-weight': String(theme.typography.bodyMedium.fontWeight),
      'font-size': theme.typography.bodyMedium.fontSize,
      'line-height': theme.typography.bodyMedium.lineHeight,
      'margin-top': DESCRIPTION_MARGIN_TOP,
      color: undefined,
    },
    'actions-container': {
      'margin-top': GAP_DEFAULT,
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
  },
});

export const getEmptyStateTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): EmptyStateDesignTokens => {
  const typography = tokens?.component?.typography;
  const spacing = tokens?.generic?.spacing;
  const charcoal700 = tokens?.generic?.color?.charcoal?.[700]?.[100];

  const sharedHeadlineMargins = {
    'margin-bottom': '0',
    'margin-left': '0',
    'margin-right': '0',
  } as const;

  const sharedActionsMargins = {
    'margin-bottom': '0',
    'margin-left': '0',
    'margin-right': '0',
  } as const;

  return {
    container: {
      'background-color':
        tokens?.semantic?.['background-color']?.surface?.[100],
      color: tokens?.semantic?.['text-color']?.medium?.default,
      'padding-top': '20px',
      'padding-bottom': '20px',
      'min-width': undefined,
      'max-width': undefined,
    },
    'illustration-container': {
      'max-height': undefined,
      'margin-top': '0',
      'margin-bottom': '0',
      'margin-left': '0',
      'margin-right': '0',
    },
    xs: {
      container: {
        'padding-left': PADDING_X_NARROW,
        'padding-right': PADDING_X_NARROW,
      },
      'illustration-container': { height: '50px', width: '50px' },
      headline: {
        'font-family': fontFamily,
        'font-weight': '400',
        'font-size': '14px',
        'line-height': '20px',
        'margin-top': GAP_XS,
        ...sharedHeadlineMargins,
      },
      description: {
        'font-family': fontFamily,
        'font-weight': '400',
        'font-size': '12px',
        'line-height': '16px',
        'margin-top': GAP_XS,
        color: charcoal700,
      },
      'actions-container': {
        'margin-top': GAP_XS,
        ...sharedActionsMargins,
      },
    },
    sm: {
      container: {
        'padding-left': PADDING_X_NARROW,
        'padding-right': PADDING_X_NARROW,
      },
      'illustration-container': { height: '100px', width: '100px' },
      headline: {
        'font-family': fontFamily,
        'font-weight': '400',
        'font-size': '20px',
        'line-height': '22px',
        'margin-top': GAP_DEFAULT,
        ...sharedHeadlineMargins,
      },
      description: {
        'font-family': fontFamily,
        'font-weight': '400',
        'font-size': '12px',
        'line-height': '16px',
        'margin-top': spacing?.[8],
        color: charcoal700,
      },
      'actions-container': {
        'margin-top': GAP_DEFAULT,
        ...sharedActionsMargins,
      },
    },
    m: {
      container: {
        'padding-left': PADDING_X_WIDE,
        'padding-right': PADDING_X_WIDE,
      },
      'illustration-container': { height: undefined, width: undefined },
      headline: {
        'font-family': fontFamily,
        ...typography?.h3,
        ...typography?.regular,
        'font-weight': String(typography?.regular?.['font-weight']),
        'margin-top': GAP_DEFAULT,
        ...sharedHeadlineMargins,
      },
      description: {
        'font-family': fontFamily,
        ...typography?.body,
        ...typography?.medium,
        'font-weight': String(typography?.medium?.['font-weight']),
        'margin-top': spacing?.[12],
        color: charcoal700,
      },
      'actions-container': {
        'margin-top': GAP_DEFAULT,
        ...sharedActionsMargins,
      },
    },
    l: {
      container: {
        'padding-left': PADDING_X_WIDE,
        'padding-right': PADDING_X_WIDE,
      },
      'illustration-container': { height: '250px', width: '250px' },
      headline: {
        'font-family': fontFamily,
        ...typography?.h1,
        ...typography?.regular,
        'font-weight': String(typography?.regular?.['font-weight']),
        'margin-top': GAP_DEFAULT,
        ...sharedHeadlineMargins,
      },
      description: {
        'font-family': fontFamily,
        ...typography?.body,
        ...typography?.medium,
        'font-weight': String(typography?.medium?.['font-weight']),
        'margin-top': spacing?.[12],
        color: charcoal700,
      },
      'actions-container': {
        'margin-top': GAP_DEFAULT,
        ...sharedActionsMargins,
      },
    },
  };
};
